import React, { useState } from 'react'

import { Box, Stack, Typography, alpha } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { LogOut as LogOutIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { PRIMARY_NAV_ITEMS, showsDesktopRail, tabNavState } from 'components/UI/primaryNav'
import { DrawerPopup } from 'components/UI/DrawerPopup'
/*
 * The app's ONE divider (`components/UI/Divider`), not a hand-rolled `borderTop`.
 *
 * NOT `@repo/ui`'s — it does not exist. `Divider` was one of the zero-byte stub
 * files there, and those were deleted rather than filled, so the import would not
 * even resolve. This is the layer-2 component (working-conventions §47) and the
 * same rule every drawer and the Notifications page section break already use.
 *
 * Its baked-in 24px margins are drawer scale; a nav rail wants less, so the
 * margins are overridden at the call site through the `sx` escape hatch the
 * component provides for exactly this.
 */
import { Divider } from 'components/UI/Divider'
import { LogOut } from 'components/pages/profile/LogOut'
import { SIDE_NAV_WIDTH } from 'constants/layout'
import { useAuth } from 'hooks'
import { font } from 'settings/tokens'

/**
 * Primary navigation as a sticky left rail, DESKTOP ONLY.
 *
 * The desktop counterpart of `BottomNav`, reading the SAME `PRIMARY_NAV_ITEMS`. It
 * is one nav with two presentations, not a second nav — see
 * `components/UI/primaryNav` for why that distinction is the whole point of this
 * change.
 *
 * The list is shared; the VISIBILITY RULE is not, and that is deliberate. The bar
 * hides where a phone's bottom edge is contested or the surface is immersive; the
 * rail is a left column with neither problem, so it uses `showsDesktopRail` and
 * stays up on a certificate, its sub-views and its Share screen. Both predicates
 * live in `primaryNav` so the divergence is one documented decision in one file
 * rather than two components quietly disagreeing.
 *
 * ## What it replaces
 *
 * Nothing, at two of the three destinations — which was the defect. `BottomNav` is
 * `display: { xs: 'flex', md: 'none' }`, so at desktop width the app's navigation
 * was a hamburger that existed only on the certificates page and offered
 * Transfers, Profile, Notifications, Help center and Log out. Certificates and
 * Activity were not in it. From `/activity` or `/profile` there was no navigation
 * at all: no bar, no hamburger, nothing but the browser's Back button.
 *
 * ## Why a rail rather than a top bar
 *
 * The bottom bar's rationale is reach, which does not exist for a pointer, so the
 * placement was re-derived rather than translated. A left rail keeps the nav out
 * of the horizontal band the certificates toolbar already owns (title, search,
 * Select, Filter, Sort, Layout — a crowded row that a nav would have to compete
 * with), and it scales to more destinations vertically, which a bar of three
 * cannot. It is also the arrangement `apps/platform` already uses, so a brand
 * operator moving between the two apps meets the same shape.
 */

interface RailRowProps {
  label: string
  icon: LucideIcon
  active?: boolean
  onClick: () => void
}

/**
 * One row. A real `<button>`, not a `div` with `tabIndex` — Tab order, Enter and
 * Space all come from the element rather than from a hand-rolled `onKeyDown`,
 * which is one fewer thing to get wrong and one fewer thing to keep in sync with
 * the bottom bar's copy of it.
 */
const RailRow: React.FC<RailRowProps> = ({ label, icon: Icon, active = false, onClick }) => (
  <Box
    component='button'
    type='button'
    onClick={onClick}
    // Only on the ACTIVE row, and only `page` — `aria-current` on every row, or
    // `aria-current="false"`, both announce as "current" in some readers.
    aria-current={active ? 'page' : undefined}
    sx={theme => ({
      // Button reset. Without these the browser's default chrome (border, grey
      // fill, centred text, 13px system font) shows through the sx.
      appearance: 'none',
      background: 'none',
      border: 0,
      font: 'inherit',
      textAlign: 'left',
      width: '100%',

      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      /*
        48 tall, and the height is EXPLICIT rather than derived from padding.

        VRG-774 raised this rail's labels 14 -> 16 to match the platform's own
        sidebar and the Filter/Sort/Layout controls beside it. It stopped at the
        type: the row stayed at `10px 12px`, i.e. 40px, while the platform's
        equivalent is `SIDEBAR_ROW_HEIGHT` = 48. So the font got the parity and
        the box did not — and a 16px label in a box sized for a 14px one reads
        TIGHTER than it did before, which is why the rail still looked short
        after a change that was supposed to fix it.

        Fixed height for the reason the platform's `SidebarList` gives at its own
        row: padding-derived height moves whenever the type does, and this rail
        has now been caught by exactly that once.
      */
      minHeight: '48px',
      padding: '0 12px',
      borderRadius: `${theme.shape.borderRadius}px`,
      cursor: 'pointer',
      color: active ? theme.palette.text.primary : theme.palette.text.secondary,
      // The active row carries a tint as well as weight. On a rail the rows sit in
      // a column with nothing else nearby to compare against, so the weight
      // difference that reads clearly in a 3-across bar is much weaker here.
      backgroundColor: active ? alpha(theme.palette.common.black, 0.05) : 'transparent',
      transition: 'background-color 120ms ease, color 120ms ease',
      '&:hover': {
        backgroundColor: active
          ? alpha(theme.palette.common.black, 0.05)
          : alpha(theme.palette.common.black, 0.03),
        color: theme.palette.text.primary,
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.text.primary}`,
        outlineOffset: '2px',
      },
    })}
  >
    {/* Weight carries the active state alongside colour and tint. Colour alone
        fails for the ~8% of men with a colour-vision deficiency, and this is the
        app's only persistent orientation cue. Same reasoning, same values as the
        bottom bar. */}
    <Icon size={20} strokeWidth={active ? 2 : 1.5} />
    <Typography
      sx={{
        // 16, not `font.label` (14). This rail is the app's TOP-LEVEL
        // navigation and sat two pixels under the Filter/Sort/Layout controls
        // beside it, which inverts the hierarchy — the page's secondary
        // controls read larger than the app's primary nav. 16 is also what the
        // platform's own sidebar uses (`SidebarList.tsx`), so the two apps'
        // rails now agree. Exactly the fix `ToolbarControl`'s ToolbarLabel
        // already carries, one component over.
        fontSize: `${font.body}px`,
        // Weight is deliberately NOT raised to the Button's 500 — see the
        // comment above: it is carrying the active state here, so a heavier
        // rest weight would narrow the gap that cue depends on.
        fontWeight: active ? 600 : 400,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
        // The rail is a fixed width, so a label that grew would push the row's
        // box rather than wrap — clip instead, and keep the row one line.
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </Typography>
  </Box>
)

export const SideNav: React.FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isAuthenticated } = useAuth()
  const [isLogOutOpen, setIsLogOutOpen] = useState(false)

  /*
   * The same navigate the bar uses, state and all. `tabNavState` is gated on
   * `innerWidth < 900`, so on desktop it returns `undefined` and the page does not
   * slide — a horizontal page slide is a phone metaphor and stays one. Calling it
   * anyway rather than special-casing keeps ONE navigation path: if the slide's
   * conditions ever change, both presentations change together.
   */
  const go = (to: string) => {
    if (to === pathname) return
    navigate(to, { state: tabNavState(pathname, to) })
  }

  /*
   * NOT `showsPrimaryNav` — that is the phone bar's rule, and applying it here is
   * what left a certificate detail page with no navigation at all on desktop (and
   * with 200px more content width than the list it was reached from). See
   * `showsDesktopRail` for the full reasoning, including why the auth check is
   * load-bearing rather than belt-and-braces.
   */
  if (!showsDesktopRail(pathname, isAuthenticated)) return null

  return (
    <>
      {/*
        THE SHELL, not the nav.

        `<nav aria-label='Primary'>` moved INWARD to wrap only the three
        destinations — see the Log out block below for why. This outer Stack is
        now pure layout: position, width, padding, scroll.
      */}
      <Stack
        sx={{
          // Desktop only, and the exact complement of `BottomNav`'s
          // `{ xs: 'flex', md: 'none' }`. Exactly one of the two is displayed at
          // any width, so only one element ever claims `aria-label='Primary'` —
          // `display: none` removes a subtree from the accessibility tree, so the
          // hidden one is not merely invisible, it is not announced. That still
          // holds with the nav nested one level in: hiding this shell hides the
          // landmark inside it.
          display: { xs: 'none', md: 'flex' },
          flex: `0 0 ${SIDE_NAV_WIDTH}px`,
          width: `${SIDE_NAV_WIDTH}px`,

          /*
            STICKY, not fixed.

            `fixed` resolves against the viewport, so the rail would leave the
            centred page column and pin itself to the window edge — at a wide
            viewport that is a rail floating in space 150px away from the content
            it navigates. Sticky keeps it in the page's own column while still
            holding position through a scroll.

            It also has to be sticky rather than fixed for the same reason the
            certificate page's header is: the tab-slide animation puts a transform
            on an ancestor, and a transformed ancestor becomes the containing block
            for `position: fixed` descendants (see `layouts/Default`).

            `alignSelf: 'flex-start'` is load-bearing — the parent row is
            `align-items: stretch` by default, which makes this a full-height
            element, and a full-height element has nothing to stick WITHIN.
          */
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          height: '100vh',
          // Long enough that the rail could outgrow the viewport if destinations
          // are added; scroll the rail rather than clipping Log out off the bottom.
          overflowY: 'auto',

          // Aligns the first row's TEXT with the page gutter's 48px: 24 here plus
          // the row's own 12px padding and a 20px glyph puts the label at 56 —
          // deliberately not exact. Matching the glyph to the gutter instead would
          // read as the icons being indented, because a glyph's optical left edge
          // sits inside its box.
          padding: '24px',
        }}
      >
        <Stack component='nav' aria-label='Primary' sx={{ gap: '4px' }}>
          {PRIMARY_NAV_ITEMS.map(({ label, icon: Icon, to, match }) => (
            <RailRow
              key={to}
              label={label}
              icon={Icon}
              // The SAME predicate the bar uses, not a re-derived one. These encode
              // real decisions — `/certificates/:id` lights Certificates because a
              // certificate is a child of that section, and `/notifications` lights
              // PROFILE because it is a settings page reached from a Profile row.
              // Re-implementing them here is how the two presentations would drift.
              active={match(pathname)}
              onClick={() => go(to)}
            />
          ))}
        </Stack>

        {/*
          Log out sits JUST BELOW the destinations, separated by a rule — not
          pinned to the bottom, which is what it used to be (`mt: 'auto'`).

          ## Why the bottom pin was wrong

          Bottom-pinning is borrowed from sidebars that are FULL — Slack's channel
          list, Linear's views. What makes a bottom item read as ANCHORED is the
          content between it and the top group. This rail has THREE items, so on a
          tall viewport the pinned row was ~950px below Profile, alone in a void:
          not "at the end of a list", just lost. The pattern does not survive a
          short nav, and the user could not find it.

          ## Why adjacency ALONE would also be wrong

          Log out is not a destination and it ENDS THE SESSION, so a mis-click
          costs more than a wrong navigation does. Flush under Profile it would
          read as a fourth tab. Proximity without a category marker trades one
          defect for a worse one.

          ## Hence the rule — the separator carries the distinction

          The divider is what says "different kind of thing", so the destination
          rows keep their styling exactly as it is. Distinguishing Log out by
          restyling Certificates / Activity / Profile would make the whole nav
          louder to solve a problem about one row.

          ## And hence it sits OUTSIDE `<nav aria-label='Primary'>`

          A logout button is not a navigation destination, so it does not belong in
          the navigation landmark — a screen-reader user listing "Primary" should
          hear three places to go, not two places and an exit. `RailRow` never
          passes `active` here, so it also carries no `aria-current`; that is a
          page-level relationship and Log out is not a page.

          The same `LogOut` confirm the hamburger and `KebabMainMenu` use, in the
          same `DrawerPopup` — it is the mitigation that makes the adjacency
          acceptable, so all three routes to logging out ask the same question the
          same way.
        */}
        <Divider sx={{ mt: '12px', mb: '12px' }} />
        <RailRow label='Log out' icon={LogOutIcon} onClick={() => setIsLogOutOpen(true)} />
      </Stack>

      {isLogOutOpen && (
        <DrawerPopup
          title='Log out'
          child={<LogOut handleClose={() => setIsLogOutOpen(false)} />}
          handleOpen={isLogOutOpen}
          handleClose={() => setIsLogOutOpen(false)}
        />
      )}
    </>
  )
}

export default SideNav
